import React, { useState, useMemo, useEffect } from "react";
import { CampaignViewEditViewFullInfoProps } from "./campaign-view-edit-view-full-info.props";
import { Box, useMediaQuery } from "@mui/material";
import { Formik, Form } from "formik";
import {
  TextInputComponent,
  SelectInputComponent,
  DescriptionInputComponent,
  RevenueInputComponent,
} from "../../../../../../common/components";
import { getUserDetails, updateLeadDetails } from "../../../../../../api/lead";
import leadInfoIcon from "../../../../../../assets/icons/leadInfo.svg";
import "./campaign-view-edit-view-full-info.scss";
import {
  initialValues,
  validationSchema,
} from "../../../../create-campaign/create-campaign-helper";
import isEqual from "lodash/isEqual";
import { isOnlyCountryCode } from "../../../../../../utils/utils";
import { useLeadOwnerOptions } from "../../../../../../utils/getLeadOwnerOptions";
import ConfirmationPopup from "../../../../../../common/components/confirmation-popup/confirmation-popup";
import AlertIcon from "../../../../../../assets/icons/alert-icon.svg";
import { useLogin } from "../../../../../../common/hooks/useUser";
import SearchableSelectOwner from "../../../../../../common/components/select-owner/select-owner-compoent";
import { OwnerOption } from "../../../../create-campaign/create-campaign";
import DatePickerComponent from "../../../../../../common/components/date-picker/date-picker-compoenent";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  STATUS_OPTIONS,
  TYPE_OPTIONS,
} from "../../../../../../constants/campaignDropdown";
import { editCampaign } from "../../../../../../api/campaign";

dayjs.extend(utc);

const CampaignViewEditViewFullInfo: React.FC<
  CampaignViewEditViewFullInfoProps
> = (props: any) => {
  const {
    campaignDetails,
    setViewFullInfoOpen,
    selectedCampaignId,
    handleCancelUpdate,
    setAlertType,
    setAlertMessage,
    setAlertOpen,
    setLoading,
    uploadedImageFile,
    removeImage,
    resetImageUpload,
    onCampaignNameUpdate,
  } = props;
  const LEAD_OWNER_OPTIONS = useLeadOwnerOptions(
    selectedCampaignId,
    campaignDetails?.owner
  );
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(true);
  const [partnerList, setPartnerList] = useState([]);
  // const [partnerListOptions, setPartnerListOptions] = useState<OptionType[]>([]);
  const { tenantDetails, userDetails } = useLogin();
  // const [selectedPartnerId, setSelectedPartnerId] = useState(campaignDetails?.assigned_to);
  const disablePartnerAssignmentBasedOnLeadStatus = ["Closed-Won", "Converted"];
  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "View Campaigns");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));
        setLeadOptions(formattedOptions);
        const campaignOwnerId = sanitize(campaignDetails?.campaignOwner?.id);
        const isOwnerInOptions = formattedOptions.some(
          (opt) => opt.value === campaignOwnerId
        );
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "Something went wrong please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    };

    fetchCreatedByOptions();
  }, []);

  const checkIsAFPSuperAdmin = () => {
    if (
      tenantDetails?.tenantType === "SuperTenant" &&
      userDetails?.roles?.role === "Super Admin"
    )
      return true;
    return false;
  };

  const checkDisableField = () => {
    return tenantDetails?.tenantType === "SuperTenant" &&
      campaignDetails?.assigned_to
      ? true
      : false;
  };

  const sanitize = (value: any): string => {
    return value === null || value === undefined || value === "null"
      ? ""
      : value;
  };

  const [showConfirmation, setShowConfirmation] = useState(false);
  const initialValues = useMemo(
    () => ({
      campaignName: sanitize(campaignDetails?.name),
      startdate: campaignDetails?.start_date
        ? dayjs.utc(campaignDetails?.start_date).local()
        : null,
      enddate: campaignDetails?.end_date
        ? dayjs.utc(campaignDetails?.end_date).local()
        : null,
      expectedRevenue: sanitize(campaignDetails?.expected_revenue),
      actualCost: sanitize(campaignDetails?.actual_cost),
      type: sanitize(campaignDetails?.type),
      status: sanitize(campaignDetails?.status),
      budgetCost: sanitize(campaignDetails?.budgeted_cost),
      expectedResponse: sanitize(campaignDetails?.expected_response),
      annualRevenue: sanitize(campaignDetails?.annual_revenue),
      description: sanitize(campaignDetails?.description),
      campaignOwner:
        sanitize(String(campaignDetails?.campaignOwner?.id)) ?? null,
    }),
    [campaignDetails]
  );

  const handleCancel = () => {
    resetImageUpload?.();
    setViewFullInfoOpen(false);
  };
  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      const campaignDetails = {
        campaignOwnerId: values.campaignOwner,
        campaignName: values.campaignName,
        startDate: values.startdate ?? null,
        endDate: values.enddate ?? null,
        type: values.type || null,
        status: values.status || null,
        budgetedCost: values.budgetCost || null,
        actualCost: values.actualCost || null,
        expectedRevenue: values.expectedRevenue || null,
        expectedResponse: values.expectedResponse || null,
        description: values.description || null,
      };

      const response = await editCampaign(selectedCampaignId, campaignDetails);

      if (response.data.success) {
        if (onCampaignNameUpdate) {
          onCampaignNameUpdate(`${values.campaignName}`);
        }
        setLoading(false);
        setAlertMessage("Campaign updated successfully!");
        setAlertType("success");
        setAlertOpen(true);
        handleCancelUpdate();
      } else {
        setAlertMessage("Failed to save campaign.");
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the campaign."
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Array.isArray(partnerList)) {
      const options = partnerList.map((partner: any) => ({
        label: partner.company_name,
        value: partner.company_name,
        id: partner.id,
      }));
      // setPartnerListOptions(options);
    }
    checkIsAFPSuperAdmin();
  }, [partnerList]);

  const isMobile = useMediaQuery("(max-width:768px)");

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(true)}
      onSubmit={(values) => handleSave(values)}
    >
      {({
        values,
        setFieldValue,
        handleSubmit,
        setFieldTouched,
        touched,
        errors,
      }) => {
        const isFormDirty = !isEqual(values, initialValues);

        return (
          <Form>
            <div className="campaign-list-view-details-view-box-full-info">
              {!isMobile && (
                <div className="campaign-list-view-details-view-box-full-info-header">
                  <div>Details</div>
                  <div>
                    {isViewOpen ? (
                      <i
                        className="fa fa-angle-down"
                        onClick={() => {
                          setIsViewOpen(!isViewOpen);
                        }}
                        aria-hidden="true"
                      />
                    ) : (
                      <i
                        className="fa fa-angle-up"
                        onClick={() => {
                          setIsViewOpen(!isViewOpen);
                        }}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                </div>
              )}
              {isViewOpen ? (
                <>
                  <div className="campaign-list-view-details-view-box-full-info-header-text">
                    Campaign Information
                  </div>
                  <div className="campaign-list-view-details-view-box-full-info-data">
                    <div className="campaign-list-view-details-view-box-full-info-data-section">
                      <div className="campaign-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="campaign-list-view-details-view-box-full-info-data-section-main">
                          Campaign Owner
                        </div>
                        <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                          <SearchableSelectOwner
                            fieldName="campaignOwner"
                            options={leadOptions}
                            isShowError={!values?.campaignOwner}
                            placeholder="Select Owner"
                            optional={true}
                            value={
                              leadOptions.find(
                                (opt) => opt.value === values?.campaignOwner
                              ) || null
                            }
                            onChange={(selectedOption: OwnerOption | null) => {
                              if (selectedOption) {
                                setFieldValue(
                                  "campaignOwner",
                                  selectedOption.value
                                ); // saving only the ID
                              } else {
                                setFieldValue("campaignOwner", null); // handle null case
                              }
                            }}
                            handleBlur={() => {
                              setFieldTouched("campaignOwner", true);
                            }}
                          />
                        </div>
                      </div>
                      <div className="campaign-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="campaign-list-view-details-view-box-full-info-data-section-main">
                          Campaign Name
                        </div>
                        <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="campaignName"
                            placeholder="Enter Campaign Name"
                            optional={true}
                            disabled={false}
                            value={values.campaignName}
                            onChange={(e: any) =>
                              setFieldValue("campaignName", e?.target?.value)
                            }
                            maxlength="100"
                          />
                        </div>
                      </div>
                      <div className="campaign-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="campaign-list-view-details-view-box-full-info-data-section-main">
                          Start Date
                        </div>
                        <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                          <DatePickerComponent
                            label="Start Date"
                            fieldName="startdate"
                            value={values?.startdate || null}
                            onChange={(date) =>
                              setFieldValue("startdate", date)
                            }
                            minDate={dayjs("1900-01-01")}
                            maxDate={values?.enddate || undefined}
                            optional={true}
                            isLabel={false}
                          />
                        </div>
                      </div>
                      <div className="campaign-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="campaign-list-view-details-view-box-full-info-data-section-main">
                          Expected Revenue
                        </div>
                        <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                          <RevenueInputComponent
                            fieldName="expectedRevenue"
                            optional={false}
                            disabled={false}
                            value={values.expectedRevenue}
                            onChange={(e) => {
                              const input = e.target.value;
                              if (
                                /^\d*\.?\d*$/.test(input) &&
                                input.length <= 15
                              ) {
                                setFieldValue("expectedRevenue", input);
                              }
                            }}
                            logo={leadInfoIcon}
                            placeholder=""
                          />
                        </div>
                      </div>
                      <div className="campaign-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="campaign-list-view-details-view-box-full-info-data-section-main">
                          Actual Cost
                        </div>
                        <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                          <RevenueInputComponent
                            fieldName="actualCost"
                            optional={false}
                            disabled={false}
                            value={values.actualCost}
                            onChange={(e) => {
                              const input = e.target.value;
                              if (
                                /^\d*\.?\d*$/.test(input) &&
                                input.length <= 15
                              ) {
                                setFieldValue("actualCost", input);
                              }
                            }}
                            logo={leadInfoIcon}
                            placeholder=""
                          />
                        </div>
                      </div>
                    </div>

                    <div className="campaign-list-view-details-view-box-full-info-data-section">
                      <div className="campaign-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="campaign-list-view-details-view-box-full-info-data-section-main">
                          Type
                        </div>
                        <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            fieldName="type"
                            value={values?.type}
                            onChange={(e: any) => {
                              setFieldValue("type", e);
                            }}
                            options={TYPE_OPTIONS}
                            optional={true}
                            isShowError={!values.type}
                            placeholder="Select Type"
                          />
                        </div>
                      </div>
                      <div className="campaign-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="campaign-list-view-details-view-box-full-info-data-section-main">
                          Status
                        </div>
                        <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            fieldName="status"
                            options={STATUS_OPTIONS}
                            onChange={(value: string) => {
                              setFieldValue("status", value);
                            }}
                            value={values.status}
                          />
                        </div>
                      </div>
                      <div className="campaign-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="campaign-list-view-details-view-box-full-info-data-section-main">
                          End Date
                        </div>
                        <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                          <DatePickerComponent
                            label="End Date"
                            fieldName="enddate"
                            value={values?.enddate || null}
                            onChange={(date) => setFieldValue("enddate", date)}
                            minDate={
                              values?.startdate
                                ? dayjs(values.startdate).isBefore(
                                    dayjs("1900-01-01")
                                  )
                                  ? dayjs("1900-01-01")
                                  : dayjs(values.startdate)
                                : undefined
                            }
                            optional={true}
                            isLabel={false}
                          />
                        </div>
                      </div>
                      <div className="campaign-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="campaign-list-view-details-view-box-full-info-data-section-main">
                          Budgeted Cost
                        </div>
                        <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                          <RevenueInputComponent
                            fieldName="budgetCost"
                            label="Budgeted Cost"
                            optional={false}
                            disabled={false}
                            value={values.budgetCost}
                            onChange={(e) => {
                              const input = e.target.value;
                              if (
                                /^\d*\.?\d*$/.test(input) &&
                                input.length <= 15
                              ) {
                                setFieldValue("budgetCost", input);
                              }
                            }}
                            logo={leadInfoIcon}
                            placeholder=""
                          />
                        </div>
                      </div>
                      <div className="campaign-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="campaign-list-view-details-view-box-full-info-data-section-main">
                          Expected Response
                        </div>
                        <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="expectedResponse"
                            placeholder="Enter Expected Response"
                            optional={false}
                            disabled={false}
                            value={values.expectedResponse}
                            onChange={(e: any) =>
                              setFieldValue(
                                "expectedResponse",
                                e?.target?.value
                              )
                            }
                            maxlength="50"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="campaign-list-view-details-view-box-full-info-header-text">
                    Description
                  </div>
                  <div className="campaign-list-view-details-view-box-full-info-description-box">
                    <DescriptionInputComponent
                      fieldName="description"
                      placeholder="Add description"
                      optional={false}
                      disabled={checkDisableField()}
                      value={values.description}
                      onChange={(e: any) =>
                        setFieldValue("description", e?.target?.value)
                      }
                      maxlength={150}
                    />
                  </div>
                </>
              ) : (
                ""
              )}
            </div>
            <Box className="footer">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  handleSubmit();
                }}
                className="cancel-button next-button-wrapper"
                disabled={!(isFormDirty || uploadedImageFile || removeImage)}
              >
                Save
              </button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CampaignViewEditViewFullInfo;
